/**
 * DM Profile Card Component
 * Shows user profile info at the top of the right sidebar for DM conversations
 */

import React from 'react';
import { formatLastSeen, statusDotColor } from '@/utils/presence.utils';
import { usePresenceStore } from '@/store/presence.store';
import type { DMThreadMember } from '@/types/api.types';

interface DMProfileCardProps {
  user: DMThreadMember;
}

export const DMProfileCard: React.FC<DMProfileCardProps> = ({ user }) => {
  const online = usePresenceStore((s) => s.onlineUserIds.has(user.id));
  const customStatus = usePresenceStore((s) => s.userStatuses.get(user.id));
  const dotColor = statusDotColor(online, customStatus);

  return (
    <div className="p-4 border-b border-gray-200 dark:border-dark-border-primary">
      <div className="flex items-center space-x-3 mb-3">
        <div className="relative">
          <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-semibold">
            {user.display_name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div
            className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#fafbfc] dark:border-dark-bg-secondary ${dotColor}`}
          />
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-dark-text-primary">
            {user.display_name || 'Unknown User'}
          </p>
          <p className="text-sm text-gray-500 dark:text-dark-text-muted">
            {user.email}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <div className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span className="text-gray-600 dark:text-dark-text-secondary">
          {formatLastSeen(user.last_seen)}
        </span>
      </div>
    </div>
  );
};
